import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Settings.css';
import FeedCatalog from '../components/FeedCatalog';
import FilterSettings from './FilterSettings';
// SaaS-специфика (онбординг/биллинг) — code-split: на выделенных деплоях
// (ИПК/Алесса) эти чанки никогда не запрашиваются с сервера.
const OnboardingTour = React.lazy(() => import('./OnboardingTour'));
const BillingSettings = React.lazy(() => import('./BillingSettings'));

function Settings({ token, onNavigate, onboardingStatus, uiProfile }) {
  const customerDeployment = uiProfile !== 'saas';
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');
  const [tourDismissed, setTourDismissed] = useState(false);
  const [filterSubTab, setFilterSubTab] = useState(null);

  // Гид (OnboardingTour) переключает не только вкладку Фильтров внутри
  // FilterSettings, но и верхнюю вкладку Настроек целиком (например
  // "Номенклатура"), поэтому живёт на уровне Settings, а не внутри одной
  // вкладки — иначе он бы размонтировался при переходе на другую вкладку.
  const handleTourNavigate = ({ topTab, subtab, section }) => {
    if (topTab) setActiveTab(topTab);
    if (subtab) setFilterSubTab(subtab);
    if (section !== undefined) setOpenSection(section);
  };
  const [exportingNomenclature, setExportingNomenclature] = useState(false);
  const [uploadingCharacteristics, setUploadingCharacteristics] = useState(false);

  // General settings
  const [generalSettings, setGeneralSettings] = useState({ timezone: 'Europe/Moscow', available_timezones: [] });
  const [savingGeneral, setSavingGeneral] = useState(false);
  // Модель GPT для сопоставления — общая настройка (не завязана на профиль/редакцию),
  // поэтому живёт в "Общих настройках", а не во вкладке "Обучение" (там осталось только
  // дообучение — отдельная, необязательная возможность).
  const [matchingModelInfo, setMatchingModelInfo] = useState(null);
  const [matchingModelSaving, setMatchingModelSaving] = useState(false);
  const [matchingModelError, setMatchingModelError] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  // Ключ OpenAI в Общих настройках (saas + alessa, не ipk — там ключ один на
  // весь выделенный инстанс, без per-company управления через UI):
  //   - saas: тот же ключ, что вводили при онбординге — здесь можно сменить
  //     его в процессе работы, без повторного онбординга.
  //   - alessa: у неё обычный онбординг никогда не проходился (шаг "API-ключ"
  //     пропущен), поэтому изначально записан чужой ключ вручную при переносе.
  // Тот же эндпоинт, что в онбординге SaaS (/api/onboarding/api-key) — просто
  // доступен и после онбординга, из Общих настроек.
  const [hasOwnOpenaiKey, setHasOwnOpenaiKey] = useState(null);
  const [openaiKeyInput, setOpenaiKeyInput] = useState('');
  const [openaiKeySaving, setOpenaiKeySaving] = useState(false);
  const [openaiKeyError, setOpenaiKeyError] = useState('');

  // Enrichment rules
  const [enrichmentRules, setEnrichmentRules] = useState([]);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [showNewEnrichment, setShowNewEnrichment] = useState(false);
  const emptyEnrichment = { trigger_synonyms: [], trigger_excludes: [], add_value: '', apply_to_categories: ['all'], description: '', is_active: true };
  const [newEnrichment, setNewEnrichment] = useState(emptyEnrichment);
  const [newTriggerInput, setNewTriggerInput] = useState('');
  const [newExcludeInput, setNewExcludeInput] = useState('');
  const [editTriggerInput, setEditTriggerInput] = useState('');
  const [editExcludeInput, setEditExcludeInput] = useState('');
  const [editingEnrichment, setEditingEnrichment] = useState(null);
  const [enrichmentMessage, setEnrichmentMessage] = useState('');
  const [enrichmentPreview, setEnrichmentPreview] = useState({ count: null, loading: false });

  // Exclusion rules
  const [exclusionRules, setExclusionRules] = useState([]);
  const [exclusionLoading, setExclusionLoading] = useState(false);
  const [showNewExclusion, setShowNewExclusion] = useState(false);
  const emptyExclusion = { trigger_synonyms: [], trigger_excludes: [], apply_to_categories: ['all'], mode: 'auto_only', description: '', is_active: true };
  const [newExclusion, setNewExclusion] = useState(emptyExclusion);
  const [newExclTriggerInput, setNewExclTriggerInput] = useState('');
  const [newExclExcludeInput, setNewExclExcludeInput] = useState('');
  const [editExclTriggerInput, setEditExclTriggerInput] = useState('');
  const [editExclExcludeInput, setEditExclExcludeInput] = useState('');
  const [editingExclusion, setEditingExclusion] = useState(null);
  const [exclusionMessage, setExclusionMessage] = useState('');
  const [exclusionPreview, setExclusionPreview] = useState({ count: null, items: [], loading: false });
  const [editExclusionPreview, setEditExclusionPreview] = useState({ count: null, items: [], loading: false });
  const [showExclusionItems, setShowExclusionItems] = useState(true);
  const [showEditExclusionItems, setShowEditExclusionItems] = useState(true);

  // Nomenclature accordion
  const [openSection, setOpenSection] = useState(null);
  const toggleSection = (name) => setOpenSection(prev => prev === name ? null : name);

  // Enrichment matched items modal
  const [matchedModal, setMatchedModal] = useState(null); // { rule, items, loading }
  const [matchedSaving, setMatchedSaving] = useState(false);

  // System logs
  const [systemLogs, setSystemLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');

  // Users management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [newUserIsPriceUploader, setNewUserIsPriceUploader] = useState(false);
  const [createUserError, setCreateUserError] = useState('');

  useEffect(() => {
    if (activeTab === 'general') {
      loadGeneralSettings();
      loadMatchingModel();
      if (uiProfile === 'alessa' || uiProfile === 'saas') loadOwnOpenaiKeyStatus();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'nomenclature') {
      fetchEnrichmentRules();
      fetchExclusionRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Server sends UTC time, convert to local timezone
    // Add 'Z' if not present to ensure it's parsed as UTC
    const utcDate = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDate);

    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Never';

    // Server sends UTC time, convert to local timezone
    // Add 'Z' if not present to ensure it's parsed as UTC
    const utcDate = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDate);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;

    return formatDate(dateString);
  };


  // General settings functions
  const loadGeneralSettings = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/settings/general`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGeneralSettings(data);
        setSelectedTimezone(data.timezone);
      }
    } catch { /* ignore */ }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/settings/general`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: selectedTimezone })
      });
      if (response.ok) {
        setGeneralSettings(prev => ({ ...prev, timezone: selectedTimezone }));
        setSuccessMessage('Настройки сохранены');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const err = await response.json();
        alert(err.detail || 'Ошибка при сохранении');
      }
    } catch { alert('Ошибка при сохранении'); }
    finally { setSavingGeneral(false); }
  };

  const loadMatchingModel = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/settings/matching-model`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setMatchingModelInfo(await response.json());
      }
    } catch { /* ignore */ }
  };

  const handleSelectMatchingModel = async (modelId) => {
    setMatchingModelSaving(true);
    setMatchingModelError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/settings/matching-model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ model: modelId }),
      });
      if (response.ok) {
        setMatchingModelInfo(prev => ({ ...prev, current: modelId }));
      } else {
        const data = await response.json();
        setMatchingModelError(data.detail || 'Ошибка сохранения');
      }
    } catch {
      setMatchingModelError('Ошибка сохранения');
    } finally {
      setMatchingModelSaving(false);
    }
  };

  const loadOwnOpenaiKeyStatus = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setHasOwnOpenaiKey(!!data.has_openai_key);
      }
    } catch { /* ignore */ }
  };

  const handleSaveOwnOpenaiKey = async (e) => {
    e.preventDefault();
    setOpenaiKeySaving(true);
    setOpenaiKeyError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/onboarding/api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ api_key: openaiKeyInput }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Не удалось сохранить ключ');
      setOpenaiKeyInput('');
      setHasOwnOpenaiKey(true);
      setSuccessMessage('Ключ OpenAI сохранён — теперь сопоставление идёт через него');
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setOpenaiKeyError(err.message);
    } finally {
      setOpenaiKeySaving(false);
    }
  };

  // Users management functions
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      // ignore
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя ${userEmail}?`)) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccessMessage(`Пользователь ${userEmail} успешно удален`);
        setTimeout(() => setSuccessMessage(''), 3000);
        loadUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Ошибка при удалении пользователя');
      }
    } catch (error) {
      alert('Ошибка при удалении пользователя');
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Пароль должен содержать минимум 6 символов');
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        setSuccessMessage(`Пароль для ${selectedUser.email} успешно изменен`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Ошибка при изменении пароля');
      }
    } catch (error) {
      alert('Ошибка при изменении пароля');
    }
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 6) return 'Пароль должен быть не менее 6 символов';
    if (!/^[A-Za-z0-9]+$/.test(pwd)) return 'Только латинские буквы и цифры';
    if (!/[A-Z]/.test(pwd)) return 'Нужна хотя бы одна заглавная буква';
    if (!/[0-9]/.test(pwd)) return 'Нужна хотя бы одна цифра';
    return '';
  };

  const handleCreateUser = async () => {
    setCreateUserError('');
    if (!newUserEmail.trim()) { setCreateUserError('Введите email'); return; }
    const pwdError = validatePassword(newUserPassword);
    if (pwdError) { setCreateUserError(pwdError); return; }

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newUserEmail.trim(), password: newUserPassword, is_admin: newUserIsAdmin, is_price_uploader: newUserIsPriceUploader })
      });

      if (response.ok) {
        setSuccessMessage(`Пользователь ${newUserEmail} создан`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowCreateUserModal(false);
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserIsAdmin(false);
        setNewUserIsPriceUploader(false);
        loadUsers();
      } else {
        const error = await response.json();
        setCreateUserError(error.detail || 'Ошибка при создании пользователя');
      }
    } catch {
      setCreateUserError('Ошибка при создании пользователя');
    }
  };

  const handleChangeRole = async (user, role) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/users/${user.id}/role`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_admin: role === 'admin', is_price_uploader: role === 'price_uploader' })
      });

      if (response.ok) {
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role } : u));
      } else {
        const error = await response.json();
        alert(error.detail || 'Ошибка при изменении роли');
      }
    } catch {
      alert('Ошибка при изменении роли');
    }
  };

  // Nomenclature export function
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Предпросмотр количества позиций при изменении триггеров/исключений
  useEffect(() => {
    const triggers = newEnrichment.trigger_synonyms;
    const excls = newEnrichment.trigger_excludes;
    if (!triggers || triggers.length === 0) {
      setEnrichmentPreview({ count: null, loading: false });
      return;
    }
    setEnrichmentPreview(prev => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          triggers: triggers.join(','),
          excludes: excls.join(','),
        });
        const r = await fetch(`${API_URL}/api/settings/enrichment-rules/preview?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          setEnrichmentPreview({ count: data.count, loading: false });
        } else {
          setEnrichmentPreview({ count: null, loading: false });
        }
      } catch {
        setEnrichmentPreview({ count: null, loading: false });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [JSON.stringify(newEnrichment.trigger_synonyms), JSON.stringify(newEnrichment.trigger_excludes)]);

  const fetchSystemLogs = async () => {
    setLogsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/settings/system-logs?lines=500`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setSystemLogs(data.logs || []);
        setShowLogs(true);
      }
    } catch (e) { /* ignore */ }
    setLogsLoading(false);
  };

  const fetchEnrichmentRules = async () => {
    setEnrichmentLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/settings/enrichment-rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setEnrichmentRules(await r.json());
    } catch (e) { /* ignore */ }
    setEnrichmentLoading(false);
  };

  const handleSaveEnrichment = async (data, id = null) => {
    const url = id ? `${API_URL}/api/settings/enrichment-rules/${id}` : `${API_URL}/api/settings/enrichment-rules`;
    const method = id ? 'PUT' : 'POST';
    const payload = {
      ...data,
      trigger_synonyms: data.trigger_synonyms || [],
      trigger_excludes: data.trigger_excludes || [],
    };
    try {
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setEnrichmentMessage(id ? 'Правило обновлено. Номенклатура пересканируется...' : 'Правило добавлено. Номенклатура пересканируется...');
        setTimeout(() => setEnrichmentMessage(''), 4000);
        setShowNewEnrichment(false);
        setEditingEnrichment(null);
        setNewEnrichment(emptyEnrichment);
        setNewTriggerInput('');
        setNewExcludeInput('');
        setEnrichmentPreview({ count: null, loading: false });
        fetchEnrichmentRules();
      }
    } catch (err) { /* ignore */ }
  };

  const openMatchedModal = async (rule) => {
    setMatchedModal({ rule, items: null, loading: true });
    try {
      const r = await fetch(`${API_URL}/api/settings/enrichment-rules/${rule.id}/matched-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setMatchedModal({ rule, items: data.items, loading: false });
      } else {
        setMatchedModal(null);
      }
    } catch (e) {
      setMatchedModal(null);
    }
  };

  const saveMatchedExclusions = async () => {
    if (!matchedModal) return;
    setMatchedSaving(true);
    const excluded = (matchedModal.items || []).filter(i => !i.is_included).map(i => i.id);
    await fetch(`${API_URL}/api/settings/enrichment-rules/${matchedModal.rule.id}/exclusions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ excluded_item_ids: excluded }),
    });
    setMatchedSaving(false);
    setMatchedModal(null);
    setEnrichmentMessage('Исключения сохранены. Номенклатура пересканируется...');
    setTimeout(() => setEnrichmentMessage(''), 4000);
    fetchEnrichmentRules();
  };

  const handleDeleteEnrichment = async (id) => {
    if (!window.confirm('Удалить правило?')) return;
    await fetch(`${API_URL}/api/settings/enrichment-rules/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchEnrichmentRules();
  };

  const fetchExclusionRules = async () => {
    setExclusionLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/settings/exclusion-rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setExclusionRules(await r.json());
    } catch (e) { /* ignore */ }
    setExclusionLoading(false);
  };

  const handleSaveExclusion = async (data, id = null) => {
    const url = id ? `${API_URL}/api/settings/exclusion-rules/${id}` : `${API_URL}/api/settings/exclusion-rules`;
    const method = id ? 'PUT' : 'POST';
    const payload = {
      ...data,
      trigger_synonyms: data.trigger_synonyms || [],
      trigger_excludes: data.trigger_excludes || [],
    };
    try {
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setExclusionMessage(id ? 'Правило обновлено. Исключения пересчитываются...' : 'Правило добавлено. Исключения пересчитываются...');
        setTimeout(() => setExclusionMessage(''), 4000);
        setShowNewExclusion(false);
        setEditingExclusion(null);
        setNewExclusion(emptyExclusion);
        setNewExclTriggerInput('');
        setNewExclExcludeInput('');
        setExclusionPreview({ count: null, items: [], loading: false });
        setEditExclusionPreview({ count: null, items: [], loading: false });
        setShowExclusionItems(true);
        setShowEditExclusionItems(true);
        fetchExclusionRules();
      }
    } catch (err) { /* ignore */ }
  };

  const handleDeleteExclusion = async (id) => {
    if (!window.confirm('Удалить правило исключения?')) return;
    await fetch(`${API_URL}/api/settings/exclusion-rules/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchExclusionRules();
  };

  // Предпросмотр для нового правила исключения.
  // Учитываем и зафиксированные chip'ы, и текущий буфер ввода — чтобы превью
  // обновлялось по мере набора, ещё до нажатия Enter.
  useEffect(() => {
    const tripBuf = newExclTriggerInput.trim().toLowerCase();
    const exclBuf = newExclExcludeInput.trim().toLowerCase();
    const triggers = [...newExclusion.trigger_synonyms, ...(tripBuf ? [tripBuf] : [])];
    const excls = [...newExclusion.trigger_excludes, ...(exclBuf ? [exclBuf] : [])];
    if (triggers.length === 0) {
      setExclusionPreview({ count: null, items: [], loading: false });
      return;
    }
    setExclusionPreview(prev => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          triggers: triggers.join(','),
          excludes: excls.join(','),
        });
        const r = await fetch(`${API_URL}/api/settings/exclusion-rules/preview?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          setExclusionPreview({ count: data.count, items: data.items || [], loading: false });
        } else {
          setExclusionPreview({ count: null, items: [], loading: false });
        }
      } catch {
        setExclusionPreview({ count: null, items: [], loading: false });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [JSON.stringify(newExclusion.trigger_synonyms), JSON.stringify(newExclusion.trigger_excludes), newExclTriggerInput, newExclExcludeInput]);

  // Предпросмотр для редактируемого правила исключения
  useEffect(() => {
    if (!editingExclusion) {
      setEditExclusionPreview({ count: null, items: [], loading: false });
      return;
    }
    const tripBuf = editExclTriggerInput.trim().toLowerCase();
    const exclBuf = editExclExcludeInput.trim().toLowerCase();
    const triggers = [...(editingExclusion.trigger_synonyms || []), ...(tripBuf ? [tripBuf] : [])];
    const excls = [...(editingExclusion.trigger_excludes || []), ...(exclBuf ? [exclBuf] : [])];
    if (triggers.length === 0) {
      setEditExclusionPreview({ count: null, items: [], loading: false });
      return;
    }
    setEditExclusionPreview(prev => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          triggers: triggers.join(','),
          excludes: excls.join(','),
        });
        const r = await fetch(`${API_URL}/api/settings/exclusion-rules/preview?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          setEditExclusionPreview({ count: data.count, items: data.items || [], loading: false });
        } else {
          setEditExclusionPreview({ count: null, items: [], loading: false });
        }
      } catch {
        setEditExclusionPreview({ count: null, items: [], loading: false });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [editingExclusion && JSON.stringify(editingExclusion.trigger_synonyms), editingExclusion && JSON.stringify(editingExclusion.trigger_excludes), editExclTriggerInput, editExclExcludeInput]);

  const handleExportNomenclature = async () => {
    setExportingNomenclature(true);
    setSuccessMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/nomenclature/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'nomenclature_export.xlsx';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) {
            filename = match[1];
          }
        }

        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccessMessage('Номенклатура успешно экспортирована');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        alert(error.detail || 'Ошибка при экспорте номенклатуры');
      }
    } catch (error) {
      alert('Ошибка при экспорте номенклатуры');
    } finally {
      setExportingNomenclature(false);
    }
  };

  // Characteristics upload function
  const handleUploadCharacteristics = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCharacteristics(true);
    setSuccessMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/nomenclature/upload-characteristics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`✅ Загружено артикулов для ${result.updated} позиций из ${result.processed}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        // Reset file input
        e.target.value = '';
      } else {
        const error = await response.json();
        alert(error.detail || 'Ошибка при загрузке артикулов');
      }
    } catch (error) {
      alert('Ошибка при загрузке артикулов');
    } finally {
      setUploadingCharacteristics(false);
    }
  };

  return (
    <div className="settings">
      {uiProfile === 'saas' && onboardingStatus === 'needs_review' && !tourDismissed && (
        <React.Suspense fallback={null}>
          <OnboardingTour
            onNavigate={handleTourNavigate}
            onDismiss={() => setTourDismissed(true)}
          />
        </React.Suspense>
      )}

      <div className="settings-header">
        <h1>⚙️ Настройки</h1>
        <p className="subtitle">Управление фильтрами и мониторинг операций</p>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          🌐 Общие настройки
        </button>
        <button
          className={`settings-tab ${activeTab === 'filters' ? 'active' : ''}`}
          onClick={() => setActiveTab('filters')}
        >
          🎛️ Фильтры
        </button>
        <button
          className={`settings-tab ${activeTab === 'monitoring' ? 'active' : ''}`}
          onClick={() => setActiveTab('monitoring')}
        >
          📊 Мониторинг
        </button>
        <button
          className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Пользователи
        </button>
        <button
          className={`settings-tab ${activeTab === 'nomenclature' ? 'active' : ''}`}
          onClick={() => setActiveTab('nomenclature')}
        >
          📦 Номенклатура
        </button>
        {!customerDeployment && (
          <button
            className={`settings-tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            💳 Оплата и Реквизиты
          </button>
        )}
      </div>

      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="tab-content">
          <div className="users-section">
            <div className="section-header">
              <h2>🌐 Общие настройки</h2>
              <p className="section-description">Базовые параметры системы</p>
            </div>

            <div style={{ maxWidth: '480px', marginTop: '24px' }}>
              <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '24px' }}>
                <h3 style={{ margin: '0 0 6px 0', fontSize: '15px' }}>🕐 Часовой пояс</h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
                  Используется для отображения времени в логах и операциях. Текущий: <strong>{generalSettings.timezone}</strong>
                </p>
                <select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '14px', marginBottom: '16px', background: 'white' }}
                >
                  {(generalSettings.available_timezones.length > 0
                    ? generalSettings.available_timezones
                    : ['UTC','Europe/Kaliningrad','Europe/Moscow','Europe/Samara','Asia/Yekaterinburg','Asia/Omsk','Asia/Krasnoyarsk','Asia/Irkutsk','Asia/Yakutsk','Asia/Vladivostok','Asia/Magadan','Asia/Kamchatka']
                  ).map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <button
                  onClick={handleSaveGeneral}
                  disabled={savingGeneral || selectedTimezone === generalSettings.timezone}
                  style={{ padding: '10px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', opacity: (savingGeneral || selectedTimezone === generalSettings.timezone) ? 0.6 : 1 }}
                >
                  {savingGeneral ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>

            <div style={{ maxWidth: '720px', marginTop: '24px' }}>
              <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '24px' }}>
                <h3 style={{ margin: '0 0 6px 0', fontSize: '15px' }}>🤖 Модель GPT для сопоставления</h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
                  Какая модель делает финальный выбор кандидата при сопоставлении. Действует, если во вкладке
                  «Фильтры → Обучение» не включена дообученная модель — она всегда в приоритете.
                </p>

                {matchingModelError && <div className="error-message">{matchingModelError}</div>}

                {matchingModelInfo && (
                  <div className="training-stats" style={{ alignItems: 'stretch' }}>
                    {matchingModelInfo.options.map((opt) => {
                      const isCurrent = matchingModelInfo.current === opt.id;
                      return (
                        <div
                          key={opt.id}
                          className="training-stat-card"
                          style={{
                            textAlign: 'left',
                            border: isCurrent ? '2px solid #4f8fc0' : '1px solid #ddd',
                            cursor: matchingModelSaving ? 'default' : 'pointer',
                            opacity: matchingModelSaving && !isCurrent ? 0.6 : 1,
                          }}
                          onClick={() => !matchingModelSaving && !isCurrent && handleSelectMatchingModel(opt.id)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <strong>{opt.label}</strong>
                            {isCurrent && <span className="job-status-badge">выбрана</span>}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 6 }}>
                            Вход: {opt.price_input} · Выход: {opt.price_output}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#444' }}>{opt.description}</div>
                          {!isCurrent && (
                            <button
                              type="button"
                              className="check-status-btn"
                              style={{ marginTop: 10 }}
                              disabled={matchingModelSaving}
                              onClick={(e) => { e.stopPropagation(); handleSelectMatchingModel(opt.id); }}
                            >
                              Выбрать
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {(uiProfile === 'alessa' || uiProfile === 'saas') && (
              <div style={{ maxWidth: '720px', marginTop: '24px' }}>
                <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '24px' }}>
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '15px' }}>🔑 API-ключ OpenAI</h3>
                  <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
                    {hasOwnOpenaiKey
                      ? 'Ключ настроен — сопоставление идёт через него. Чтобы использовать свой собственный аккаунт OpenAI (отдельный лимит и биллинг), введите новый ключ ниже — он заменит текущий.'
                      : 'Ключ пока не задан. Введите его ниже, чтобы сопоставление заказов заработало.'}
                  </p>
                  {openaiKeyError && <div className="error-message" style={{ marginBottom: 12 }}>{openaiKeyError}</div>}
                  <form onSubmit={handleSaveOwnOpenaiKey} style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={openaiKeyInput}
                      onChange={(e) => setOpenaiKeyInput(e.target.value)}
                      placeholder="sk-..."
                      required
                      style={{ flex: 1, padding: '10px 12px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <button
                      type="submit"
                      disabled={openaiKeySaving}
                      style={{ padding: '10px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', opacity: openaiKeySaving ? 0.6 : 1 }}
                    >
                      {openaiKeySaving ? 'Проверка...' : 'Сохранить'}
                    </button>
                  </form>
                  <p style={{ margin: '16px 0 0 0', fontSize: '13px', color: '#666' }}>
                    Получить ключ можно на{' '}
                    <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>
                    {' '}— войдите в свой аккаунт OpenAI (или зарегистрируйте новый), откройте раздел «API keys» и нажмите
                    «Create new secret key». Формат ключа: <code>sk-...</code>, показывается только один раз при создании —
                    сразу скопируйте его сюда. Запросы к GPT оплачиваются напрямую в OpenAI по вашему ключу, отдельно от
                    подписки на ИИ:Матчер.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters Tab */}
      {activeTab === 'filters' && (
        <div className="tab-content">
          <FilterSettings
            token={token}
            forcedSubTab={filterSubTab}
            onSubTabChange={setFilterSubTab}
          />
        </div>
      )}

      {/* Billing Tab */}
      {!customerDeployment && activeTab === 'billing' && (
        <div className="tab-content">
          <React.Suspense fallback={null}>
            <BillingSettings token={token} />
          </React.Suspense>
        </div>
      )}

      {/* Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <div className="tab-content">
          {/* System Logs Panel */}
          <div style={{ marginBottom: '24px', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#f8f9fa', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => { if (!showLogs) fetchSystemLogs(); else setShowLogs(false); }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '18px' }}>🖥️</span>
                <div>
                  <strong style={{ fontSize: '15px' }}>Системные логи бэкенда</strong>
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>Последние 500 строк из буфера приложения</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {showLogs && (
                  <button
                    style={{ padding: '5px 12px', borderRadius: '5px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}
                    onClick={(e) => { e.stopPropagation(); fetchSystemLogs(); }}
                    disabled={logsLoading}
                  >
                    {logsLoading ? '⏳' : '🔄 Обновить'}
                  </button>
                )}
                <span style={{ fontSize: '18px', color: '#999' }}>{showLogs ? '▲' : '▼'}</span>
              </div>
            </div>
            {showLogs && (
              <div style={{ padding: '0' }}>
                {logsLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Загрузка логов...</div>
                ) : systemLogs && systemLogs.length > 0 ? (
                  <pre style={{
                    margin: 0,
                    padding: '12px 16px',
                    background: '#1e1e2e',
                    color: '#cdd6f4',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    maxHeight: '500px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}>
                    {systemLogs.join('\n')}
                  </pre>
                ) : (
                  <div style={{ padding: '20px', color: '#999', fontSize: '14px' }}>Логов пока нет (буфер пуст после перезапуска)</div>
                )}
              </div>
            )}
            {!showLogs && !logsLoading && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid #e0e0e0' }}>
                <button className="refresh-btn" onClick={(e) => { e.stopPropagation(); fetchSystemLogs(); }} disabled={logsLoading}>
                  {logsLoading ? '⏳ Загрузка...' : '📋 Посмотреть последние логи'}
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="tab-content">
          <div className="users-section">
            <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h2>👥 Управление пользователями</h2>
                <p className="section-description">Просмотр, удаление и управление пользователями</p>
              </div>
              <button
                onClick={() => { setShowCreateUserModal(true); setCreateUserError(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserIsAdmin(false); setNewUserIsPriceUploader(false); }}
                style={{ padding: '8px 18px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
              >
                + Добавить пользователя
              </button>
            </div>

            {loadingUsers ? (
              <div className="loading">Загрузка пользователей...</div>
            ) : users.length > 0 ? (
              <div className="users-table-container">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th style={{ textAlign: 'center' }}>Роль</th>
                      <th>Дата создания</th>
                      <th>Последний вход</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="count-cell">{user.id}</td>
                        <td className="operation-cell"><strong>{user.email}</strong></td>
                        <td style={{ textAlign: 'center' }}>
                          <select value={user.role} onChange={(e) => handleChangeRole(user, e.target.value)}>
                            <option value="user">Пользователь</option>
                            <option value="price_uploader">Загрузка прайсов</option>
                            <option value="admin">Администратор</option>
                          </select>
                        </td>
                        <td className="date-cell">{formatDate(user.created_at)}</td>
                        <td className="date-cell">{getRelativeTime(user.last_login)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="action-btn-small primary"
                            onClick={() => handleChangePassword(user)}
                            style={{ marginRight: '8px', padding: '5px 10px', fontSize: '13px' }}
                          >
                            🔑 Пароль
                          </button>
                          <button
                            className="action-btn-small danger"
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            style={{ padding: '5px 10px', fontSize: '13px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            🗑️ Удалить
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-logs">
                <p>Нет зарегистрированных пользователей</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nomenclature Tab */}
      {activeTab === 'nomenclature' && (
        <div className="tab-content">
          {uiProfile === 'saas' && <FeedCatalog token={token} />}
          {[
            { key: 'export', icon: '📥', title: 'Экспорт номенклатуры', desc: 'Выгрузка в Excel с расширенными описаниями' },
            { key: 'upload', icon: '📤', title: 'Загрузка артикулов', desc: 'Сопоставление по одинаковым артикулам из Excel' },
            { key: 'enrichment', icon: '🔧', title: 'Правила обогащения описания', desc: `Автоматическое добавление характеристик по признакам (${enrichmentRules.length})` },
            { key: 'exclusion', icon: '🚫', title: 'Правила исключения', desc: `Позиции, которые не попадут в автосопоставление или сопоставление полностью (${exclusionRules.length})` },
          ].map(({ key, icon, title, desc }) => (
            <div key={key} style={{ marginBottom: '8px', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: openSection === key ? '#f0f4ff' : '#f8f9fa', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSection(key)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>{icon}</span>
                  <div>
                    <strong style={{ fontSize: '15px' }}>{title}</strong>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>{desc}</div>
                  </div>
                </div>
                <span style={{ fontSize: '16px', color: '#999' }}>{openSection === key ? '▲' : '▼'}</span>
              </div>

              {openSection === key && (
                <div style={{ padding: '20px', borderTop: '1px solid #e0e0e0' }}>
                  {/* Export */}
                  {key === 'export' && (
                    <>
                      <p>Экспортируйте текущую номенклатуру в Excel файл с двумя колонками:</p>
                      <ul style={{ marginLeft: '20px', marginTop: '10px', marginBottom: '20px' }}>
                        <li><strong>Название</strong> — название позиции номенклатуры</li>
                        <li><strong>Расширенное описание</strong> — полные характеристики позиции</li>
                      </ul>
                      <p style={{ marginBottom: '20px' }}>Расширенные описания используются для более точного сопоставления позиций с помощью GPT.</p>
                      <button className="generate-key-btn" onClick={handleExportNomenclature} disabled={exportingNomenclature}>
                        {exportingNomenclature ? '⏳ Экспорт...' : '📥 Экспортировать номенклатуру'}
                      </button>
                    </>
                  )}

                  {/* Upload */}
                  {key === 'upload' && (
                    <>
                      <p>Загрузите Excel файл с артикулами — 2 колонки: название позиции номенклатуры и её артикул:</p>
                      <ul style={{ marginLeft: '20px', marginTop: '10px', marginBottom: '20px' }}>
                        <li>Первая колонка — <strong>"Название"</strong> (название позиции как в номенклатуре)</li>
                        <li>Вторая колонка — <strong>"Артикул"</strong></li>
                        <li>Артикулы используются для автоматического сопоставления: если у позиции заказа и позиции номенклатуры совпадает артикул — они считаются одним и тем же товаром</li>
                        <li>Если позиция найдена по названию — артикул из файла <strong>добавится</strong> к ней (уже загруженные ранее артикулы не удаляются)</li>
                        <li>Если позиция не найдена — ничего не изменится</li>
                      </ul>
                      <label htmlFor="characteristics-file" className="generate-key-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                        {uploadingCharacteristics ? '⏳ Загрузка...' : '📤 Выбрать файл с артикулами'}
                      </label>
                      <input id="characteristics-file" type="file" accept=".xlsx,.xls,.csv" onChange={handleUploadCharacteristics} disabled={uploadingCharacteristics} style={{ display: 'none' }} />
                      <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', fontSize: '14px' }}>
                        <strong>💡 Совет:</strong> Можно загружать несколько файлов последовательно — каждая загрузка обновит только те позиции, которые есть в файле.
                      </div>
                    </>
                  )}

                  {/* Enrichment */}
                  {key === 'enrichment' && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#555' }}>Если текст позиции содержит указанный признак — автоматически добавляется значение в характеристики. При изменении правил номенклатура пересканируется автоматически.</p>
                      {enrichmentMessage && <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#d4edda', color: '#155724', borderRadius: '6px', fontSize: '14px' }}>{enrichmentMessage}</div>}
                      <button className="generate-key-btn" onClick={() => setShowNewEnrichment(true)} style={{ marginBottom: '16px' }}>+ Добавить правило</button>

                      {showNewEnrichment && (
                        <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
                          <h4 style={{ marginTop: 0 }}>Новое правило</h4>
                          <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#155724', fontWeight: 500 }}>Содержит (все слова)</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                {newEnrichment.trigger_synonyms.map(t => (
                                  <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {t}
                                    <button onClick={() => setNewEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.filter(x => x !== t) }))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#155724', fontSize: '14px', lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input type="text" placeholder="плоскогубцы" value={newTriggerInput}
                                  onChange={e => setNewTriggerInput(e.target.value)}
                                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && newTriggerInput.trim()) { e.preventDefault(); const w = newTriggerInput.trim().toLowerCase(); setNewEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setNewTriggerInput(''); }}}
                                  style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #b8dabd', fontSize: '13px' }} />
                                <button onClick={() => { const w = newTriggerInput.trim().toLowerCase(); if (w) { setNewEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setNewTriggerInput(''); }}}
                                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #b8dabd', cursor: 'pointer', background: '#d4edda', fontSize: '13px' }}>+</button>
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#721c24', fontWeight: 500 }}>Не содержит</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                {newEnrichment.trigger_excludes.map(t => (
                                  <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {t}
                                    <button onClick={() => setNewEnrichment(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.filter(x => x !== t) }))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#721c24', fontSize: '14px', lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input type="text" placeholder="китай" value={newExcludeInput}
                                  onChange={e => setNewExcludeInput(e.target.value)}
                                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && newExcludeInput.trim()) { e.preventDefault(); const w = newExcludeInput.trim().toLowerCase(); setNewEnrichment(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.includes(w) ? prev.trigger_excludes : [...prev.trigger_excludes, w] })); setNewExcludeInput(''); }}}
                                  style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #f5c6cb', fontSize: '13px' }} />
                                <button onClick={() => { const w = newExcludeInput.trim().toLowerCase(); if (w) { setNewEnrichment(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.includes(w) ? prev.trigger_excludes : [...prev.trigger_excludes, w] })); setNewExcludeInput(''); }}}
                                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #f5c6cb', cursor: 'pointer', background: '#f8d7da', fontSize: '13px' }}>+</button>
                              </div>
                            </div>
                          </div>
                          {enrichmentPreview.count !== null || enrichmentPreview.loading ? (
                            <div style={{ marginBottom: '10px', fontSize: '13px', color: enrichmentPreview.loading ? '#999' : '#1a56db' }}>
                              {enrichmentPreview.loading ? 'Поиск...' : `${enrichmentPreview.count} позиций найдено`}
                            </div>
                          ) : null}
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Добавить значение</label>
                            <input type="text" placeholder="9хс" value={newEnrichment.add_value}
                              onChange={e => setNewEnrichment({...newEnrichment, add_value: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Описание (необязательно)</label>
                            <input type="text" placeholder="Метчики м-р всегда из 9ХС" value={newEnrichment.description || ''}
                              onChange={e => setNewEnrichment({...newEnrichment, description: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="generate-key-btn" onClick={() => handleSaveEnrichment(newEnrichment)} disabled={newEnrichment.trigger_synonyms.length === 0 || !newEnrichment.add_value}>Сохранить</button>
                            <button onClick={() => { setShowNewEnrichment(false); setNewEnrichment(emptyEnrichment); setNewTriggerInput(''); setNewExcludeInput(''); setEnrichmentPreview({ count: null, loading: false }); }}
                              style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>Отмена</button>
                          </div>
                        </div>
                      )}

                      {enrichmentLoading && <div style={{ color: '#666', fontSize: '14px' }}>Загрузка...</div>}
                      {enrichmentRules.length === 0 && !enrichmentLoading && !showNewEnrichment && (
                        <div style={{ color: '#999', fontSize: '14px' }}>Правил пока нет. Нажмите «+ Добавить правило».</div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {enrichmentRules.map(rule => (
                          <div key={rule.id} style={{ padding: '12px 16px', background: rule.is_active ? '#fff' : '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', opacity: rule.is_active ? 1 : 0.7 }}>
                            {editingEnrichment?.id === rule.id ? (
                              <div>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px', color: '#155724', fontWeight: 500 }}>Содержит (все слова)</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                                      {(editingEnrichment.trigger_synonyms || []).map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {t}
                                          <button onClick={() => setEditingEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.filter(x => x !== t) }))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#155724', fontSize: '13px', lineHeight: 1 }}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input type="text" placeholder="добавить слово" value={editTriggerInput}
                                        onChange={e => setEditTriggerInput(e.target.value)}
                                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && editTriggerInput.trim()) { e.preventDefault(); const w = editTriggerInput.trim().toLowerCase(); setEditingEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setEditTriggerInput(''); }}}
                                        style={{ flex: 1, padding: '4px', borderRadius: '4px', border: '1px solid #b8dabd', fontSize: '12px' }} />
                                      <button onClick={() => { const w = editTriggerInput.trim().toLowerCase(); if (w) { setEditingEnrichment(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setEditTriggerInput(''); }}}
                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #b8dabd', cursor: 'pointer', background: '#d4edda', fontSize: '12px' }}>+</button>
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px', color: '#721c24', fontWeight: 500 }}>Не содержит</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                                      {(editingEnrichment.trigger_excludes || []).map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {t}
                                          <button onClick={() => setEditingEnrichment(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.filter(x => x !== t) }))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#721c24', fontSize: '13px', lineHeight: 1 }}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input type="text" placeholder="добавить слово" value={editExcludeInput}
                                        onChange={e => setEditExcludeInput(e.target.value)}
                                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && editExcludeInput.trim()) { e.preventDefault(); const w = editExcludeInput.trim().toLowerCase(); setEditingEnrichment(prev => ({ ...prev, trigger_excludes: (prev.trigger_excludes||[]).includes(w) ? prev.trigger_excludes : [...(prev.trigger_excludes||[]), w] })); setEditExcludeInput(''); }}}
                                        style={{ flex: 1, padding: '4px', borderRadius: '4px', border: '1px solid #f5c6cb', fontSize: '12px' }} />
                                      <button onClick={() => { const w = editExcludeInput.trim().toLowerCase(); if (w) { setEditingEnrichment(prev => ({ ...prev, trigger_excludes: (prev.trigger_excludes||[]).includes(w) ? prev.trigger_excludes : [...(prev.trigger_excludes||[]), w] })); setEditExcludeInput(''); }}}
                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #f5c6cb', cursor: 'pointer', background: '#f8d7da', fontSize: '12px' }}>+</button>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px' }}>Значение</label>
                                  <input type="text" value={editingEnrichment.add_value}
                                    onChange={e => setEditingEnrichment({...editingEnrichment, add_value: e.target.value})}
                                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                  <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px' }}>Описание</label>
                                  <input type="text" value={editingEnrichment.description || ''}
                                    onChange={e => setEditingEnrichment({...editingEnrichment, description: e.target.value})}
                                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button className="generate-key-btn" onClick={() => handleSaveEnrichment(editingEnrichment, rule.id)}>Сохранить</button>
                                  <button onClick={() => { setEditingEnrichment(null); setEditTriggerInput(''); setEditExcludeInput(''); }} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>Отмена</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {(rule.trigger_synonyms || []).map(t => (
                                      <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '13px' }}>{t}</span>
                                    ))}
                                    {(rule.trigger_excludes || []).length > 0 && <>
                                      <span style={{ color: '#999', fontSize: '12px' }}>не</span>
                                      {rule.trigger_excludes.map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '13px' }}>{t}</span>
                                      ))}
                                    </>}
                                    <span style={{ color: '#666', fontSize: '14px' }}>→</span>
                                    <strong style={{ fontSize: '14px' }}>{rule.add_value}</strong>
                                  </div>
                                  {rule.description && <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>{rule.description}</div>}
                                  <button
                                    onClick={() => openMatchedModal(rule)}
                                    style={{ marginTop: '4px', background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: '13px', padding: 0, textDecoration: 'underline' }}
                                  >
                                    Посмотреть позиции{(rule.excluded_item_ids || []).length > 0 ? ` (${(rule.excluded_item_ids).length} исключено)` : ''}
                                  </button>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={rule.is_active}
                                      onChange={() => handleSaveEnrichment({ trigger_synonyms: rule.trigger_synonyms, trigger_excludes: rule.trigger_excludes, add_value: rule.add_value, apply_to_categories: rule.apply_to_categories, description: rule.description, is_active: !rule.is_active, sort_order: rule.sort_order }, rule.id)} />
                                    {rule.is_active ? 'Активно' : 'Выкл.'}
                                  </label>
                                  <button onClick={() => { setEditingEnrichment({ ...rule, trigger_excludes: rule.trigger_excludes || [] }); setEditTriggerInput(''); setEditExcludeInput(''); }}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '13px', background: '#fff' }}>Изменить</button>
                                  <button onClick={() => handleDeleteEnrichment(rule.id)}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #fcc', cursor: 'pointer', fontSize: '13px', background: '#fff4f4', color: '#c00' }}>Удалить</button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Exclusion */}
                  {key === 'exclusion' && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#555' }}>
                        Правила, по которым позиции номенклатуры исключаются из процесса сопоставления.
                        Режим «только автосопоставление» — GPT всё равно выбирает из этих позиций, но автоматическое совпадение никогда не присваивается.
                        Режим «полностью» — позиции полностью выбрасываются из кандидатов, фильтров и обучающих данных.
                      </p>
                      {exclusionMessage && <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#d4edda', color: '#155724', borderRadius: '6px', fontSize: '14px' }}>{exclusionMessage}</div>}
                      <button className="generate-key-btn" onClick={() => setShowNewExclusion(true)} style={{ marginBottom: '16px' }}>+ Добавить правило</button>

                      {showNewExclusion && (
                        <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
                          <h4 style={{ marginTop: 0 }}>Новое правило исключения</h4>
                          <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#155724', fontWeight: 500 }}>Содержит (все слова)</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                {newExclusion.trigger_synonyms.map(t => (
                                  <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {t}
                                    <button onClick={() => setNewExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.filter(x => x !== t) }))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#155724', fontSize: '14px', lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input type="text" placeholder="сверло" value={newExclTriggerInput}
                                  onChange={e => setNewExclTriggerInput(e.target.value)}
                                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && newExclTriggerInput.trim()) { e.preventDefault(); const w = newExclTriggerInput.trim().toLowerCase(); setNewExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setNewExclTriggerInput(''); }}}
                                  style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #b8dabd', fontSize: '13px' }} />
                                <button onClick={() => { const w = newExclTriggerInput.trim().toLowerCase(); if (w) { setNewExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setNewExclTriggerInput(''); }}}
                                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #b8dabd', cursor: 'pointer', background: '#d4edda', fontSize: '13px' }}>+</button>
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#721c24', fontWeight: 500 }}>Не содержит</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                {newExclusion.trigger_excludes.map(t => (
                                  <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {t}
                                    <button onClick={() => setNewExclusion(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.filter(x => x !== t) }))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#721c24', fontSize: '14px', lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input type="text" placeholder="центр" value={newExclExcludeInput}
                                  onChange={e => setNewExclExcludeInput(e.target.value)}
                                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && newExclExcludeInput.trim()) { e.preventDefault(); const w = newExclExcludeInput.trim().toLowerCase(); setNewExclusion(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.includes(w) ? prev.trigger_excludes : [...prev.trigger_excludes, w] })); setNewExclExcludeInput(''); }}}
                                  style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #f5c6cb', fontSize: '13px' }} />
                                <button onClick={() => { const w = newExclExcludeInput.trim().toLowerCase(); if (w) { setNewExclusion(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.includes(w) ? prev.trigger_excludes : [...prev.trigger_excludes, w] })); setNewExclExcludeInput(''); }}}
                                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #f5c6cb', cursor: 'pointer', background: '#f8d7da', fontSize: '13px' }}>+</button>
                              </div>
                            </div>
                          </div>
                          {exclusionPreview.count !== null || exclusionPreview.loading ? (
                            <div style={{ marginBottom: '10px', fontSize: '13px' }}>
                              {exclusionPreview.loading ? (
                                <span style={{ color: '#999' }}>Поиск...</span>
                              ) : (
                                <>
                                  <span style={{ color: '#1a56db' }}>{exclusionPreview.count} позиций найдено</span>
                                  {exclusionPreview.items && exclusionPreview.items.length > 0 && (
                                    <button onClick={() => setShowExclusionItems(v => !v)}
                                      style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '13px' }}>
                                      {showExclusionItems ? 'Скрыть список' : 'Показать список'}
                                    </button>
                                  )}
                                </>
                              )}
                              {showExclusionItems && exclusionPreview.items && exclusionPreview.items.length > 0 && (
                                <div style={{ marginTop: '8px', padding: '10px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', maxHeight: '240px', overflowY: 'auto', fontSize: '12px' }}>
                                  {exclusionPreview.items.map((it, idx) => (
                                    <div key={idx} style={{ padding: '2px 0', borderBottom: idx < exclusionPreview.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>{typeof it === 'string' ? it : it?.name}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null}
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>Режим исключения</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', cursor: 'pointer', fontSize: '13px' }}>
                              <input type="radio" name="new-excl-mode" checked={newExclusion.mode === 'auto_only'}
                                onChange={() => setNewExclusion({...newExclusion, mode: 'auto_only'})} />
                              <span><strong>Только автосопоставление</strong> — GPT видит позицию, но авто-результат никогда не присваивается</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                              <input type="radio" name="new-excl-mode" checked={newExclusion.mode === 'full'}
                                onChange={() => setNewExclusion({...newExclusion, mode: 'full'})} />
                              <span><strong>Полностью</strong> — позиция полностью выбрасывается из кандидатов (в т.ч. учебных данных)</span>
                            </label>
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Описание (необязательно)</label>
                            <input type="text" placeholder="Не использовать старые серии" value={newExclusion.description || ''}
                              onChange={e => setNewExclusion({...newExclusion, description: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="generate-key-btn" onClick={() => handleSaveExclusion(newExclusion)} disabled={newExclusion.trigger_synonyms.length === 0}>Сохранить</button>
                            <button onClick={() => { setShowNewExclusion(false); setNewExclusion(emptyExclusion); setNewExclTriggerInput(''); setNewExclExcludeInput(''); setExclusionPreview({ count: null, items: [], loading: false }); setShowExclusionItems(true); }}
                              style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>Отмена</button>
                          </div>
                        </div>
                      )}

                      {exclusionLoading && <div style={{ color: '#666', fontSize: '14px' }}>Загрузка...</div>}
                      {exclusionRules.length === 0 && !exclusionLoading && !showNewExclusion && (
                        <div style={{ color: '#999', fontSize: '14px' }}>Правил пока нет. Нажмите «+ Добавить правило».</div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {exclusionRules.map(rule => (
                          <div key={rule.id} style={{ padding: '12px 16px', background: rule.is_active ? '#fff' : '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', opacity: rule.is_active ? 1 : 0.7 }}>
                            {editingExclusion?.id === rule.id ? (
                              <div>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px', color: '#155724', fontWeight: 500 }}>Содержит (все слова)</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                                      {(editingExclusion.trigger_synonyms || []).map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {t}
                                          <button onClick={() => setEditingExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.filter(x => x !== t) }))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#155724', fontSize: '13px', lineHeight: 1 }}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input type="text" placeholder="добавить слово" value={editExclTriggerInput}
                                        onChange={e => setEditExclTriggerInput(e.target.value)}
                                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && editExclTriggerInput.trim()) { e.preventDefault(); const w = editExclTriggerInput.trim().toLowerCase(); setEditingExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setEditExclTriggerInput(''); }}}
                                        style={{ flex: 1, padding: '4px', borderRadius: '4px', border: '1px solid #b8dabd', fontSize: '12px' }} />
                                      <button onClick={() => { const w = editExclTriggerInput.trim().toLowerCase(); if (w) { setEditingExclusion(prev => ({ ...prev, trigger_synonyms: prev.trigger_synonyms.includes(w) ? prev.trigger_synonyms : [...prev.trigger_synonyms, w] })); setEditExclTriggerInput(''); }}}
                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #b8dabd', cursor: 'pointer', background: '#d4edda', fontSize: '12px' }}>+</button>
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px', color: '#721c24', fontWeight: 500 }}>Не содержит</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                                      {(editingExclusion.trigger_excludes || []).map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {t}
                                          <button onClick={() => setEditingExclusion(prev => ({ ...prev, trigger_excludes: prev.trigger_excludes.filter(x => x !== t) }))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#721c24', fontSize: '13px', lineHeight: 1 }}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input type="text" placeholder="добавить слово" value={editExclExcludeInput}
                                        onChange={e => setEditExclExcludeInput(e.target.value)}
                                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && editExclExcludeInput.trim()) { e.preventDefault(); const w = editExclExcludeInput.trim().toLowerCase(); setEditingExclusion(prev => ({ ...prev, trigger_excludes: (prev.trigger_excludes||[]).includes(w) ? prev.trigger_excludes : [...(prev.trigger_excludes||[]), w] })); setEditExclExcludeInput(''); }}}
                                        style={{ flex: 1, padding: '4px', borderRadius: '4px', border: '1px solid #f5c6cb', fontSize: '12px' }} />
                                      <button onClick={() => { const w = editExclExcludeInput.trim().toLowerCase(); if (w) { setEditingExclusion(prev => ({ ...prev, trigger_excludes: (prev.trigger_excludes||[]).includes(w) ? prev.trigger_excludes : [...(prev.trigger_excludes||[]), w] })); setEditExclExcludeInput(''); }}}
                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #f5c6cb', cursor: 'pointer', background: '#f8d7da', fontSize: '12px' }}>+</button>
                                    </div>
                                  </div>
                                </div>
                                {editExclusionPreview.count !== null || editExclusionPreview.loading ? (
                                  <div style={{ marginBottom: '8px', fontSize: '12px' }}>
                                    {editExclusionPreview.loading ? (
                                      <span style={{ color: '#999' }}>Поиск...</span>
                                    ) : (
                                      <>
                                        <span style={{ color: '#1a56db' }}>{editExclusionPreview.count} позиций найдено</span>
                                        {editExclusionPreview.items && editExclusionPreview.items.length > 0 && (
                                          <button onClick={() => setShowEditExclusionItems(v => !v)}
                                            style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '12px' }}>
                                            {showEditExclusionItems ? 'Скрыть' : 'Показать список'}
                                          </button>
                                        )}
                                      </>
                                    )}
                                    {showEditExclusionItems && editExclusionPreview.items && editExclusionPreview.items.length > 0 && (
                                      <div style={{ marginTop: '6px', padding: '8px', background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto', fontSize: '11px' }}>
                                        {editExclusionPreview.items.map((it, idx) => (
                                          <div key={idx} style={{ padding: '2px 0', borderBottom: idx < editExclusionPreview.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>{typeof it === 'string' ? it : it?.name}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                                <div style={{ marginBottom: '8px' }}>
                                  <label style={{ fontSize: '13px', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Режим</label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                    <input type="radio" name={`edit-excl-mode-${rule.id}`} checked={editingExclusion.mode === 'auto_only'}
                                      onChange={() => setEditingExclusion({...editingExclusion, mode: 'auto_only'})} />
                                    Только автосопоставление
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px' }}>
                                    <input type="radio" name={`edit-excl-mode-${rule.id}`} checked={editingExclusion.mode === 'full'}
                                      onChange={() => setEditingExclusion({...editingExclusion, mode: 'full'})} />
                                    Полностью
                                  </label>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                  <label style={{ fontSize: '13px', display: 'block', marginBottom: '3px' }}>Описание</label>
                                  <input type="text" value={editingExclusion.description || ''}
                                    onChange={e => setEditingExclusion({...editingExclusion, description: e.target.value})}
                                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button className="generate-key-btn" onClick={() => handleSaveExclusion(editingExclusion, rule.id)}>Сохранить</button>
                                  <button onClick={() => { setEditingExclusion(null); setEditExclTriggerInput(''); setEditExclExcludeInput(''); setEditExclusionPreview({ count: null, items: [], loading: false }); setShowEditExclusionItems(true); }}
                                    style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>Отмена</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {(rule.trigger_synonyms || []).map(t => (
                                      <span key={t} style={{ padding: '2px 8px', background: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '13px' }}>{t}</span>
                                    ))}
                                    {(rule.trigger_excludes || []).length > 0 && <>
                                      <span style={{ color: '#999', fontSize: '12px' }}>не</span>
                                      {rule.trigger_excludes.map(t => (
                                        <span key={t} style={{ padding: '2px 8px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '13px' }}>{t}</span>
                                      ))}
                                    </>}
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      background: rule.mode === 'full' ? '#f8d7da' : '#fff3cd',
                                      color: rule.mode === 'full' ? '#721c24' : '#856404',
                                      marginLeft: '4px'
                                    }}>
                                      {rule.mode === 'full' ? '🚫 Полностью' : '⚠️ Только авто'}
                                    </span>
                                  </div>
                                  {rule.description && <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>{rule.description}</div>}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={rule.is_active}
                                      onChange={() => handleSaveExclusion({ trigger_synonyms: rule.trigger_synonyms, trigger_excludes: rule.trigger_excludes, apply_to_categories: rule.apply_to_categories, mode: rule.mode, description: rule.description, is_active: !rule.is_active, sort_order: rule.sort_order }, rule.id)} />
                                    {rule.is_active ? 'Активно' : 'Выкл.'}
                                  </label>
                                  <button onClick={() => { setEditingExclusion({ ...rule, trigger_excludes: rule.trigger_excludes || [] }); setEditExclTriggerInput(''); setEditExclExcludeInput(''); }}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '13px', background: '#fff' }}>Изменить</button>
                                  <button onClick={() => handleDeleteExclusion(rule.id)}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #fcc', cursor: 'pointer', fontSize: '13px', background: '#fff4f4', color: '#c00' }}>Удалить</button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3>Изменить пароль</h3>
            <p>Пользователь: <strong>{selectedUser.email}</strong></p>
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Новый пароль:
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Минимум 6 символов"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSavePassword}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Сохранить
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUser(null);
                  setNewPassword('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '420px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Создать пользователя</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Email:</label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                style={{ width: '100%', padding: '10px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Пароль:</label>
              <input
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Мин. 6 символов, латиница, заглавная буква, цифра"
                style={{ width: '100%', padding: '10px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Только латиница и цифры, хотя бы одна заглавная и одна цифра</div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Роль:</label>
              <select
                value={newUserIsAdmin ? 'admin' : (newUserIsPriceUploader ? 'price_uploader' : 'user')}
                onChange={(e) => { setNewUserIsAdmin(e.target.value === 'admin'); setNewUserIsPriceUploader(e.target.value === 'price_uploader'); }}
                style={{ width: '100%', padding: '10px', border: '2px solid #e9ecef', borderRadius: '6px' }}
              >
                <option value="user">Пользователь</option>
                <option value="price_uploader">Только загрузка прайсов</option>
                <option value="admin">Администратор</option>
              </select>
            </div>
            {createUserError && (
              <div style={{ marginBottom: '16px', padding: '8px 12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', color: '#856404', fontSize: '13px' }}>
                {createUserError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCreateUser}
                style={{ flex: 1, padding: '10px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Создать
              </button>
              <button
                onClick={() => { setShowCreateUserModal(false); setCreateUserError(''); }}
                style={{ flex: 1, padding: '10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-footer">
        <button className="back-btn" onClick={() => onNavigate('dashboard')}>
          ← Вернуться на главную
        </button>
      </div>

      {/* Matched items modal */}
      {matchedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px' }}>
                  Позиции для правила: <em>{(matchedModal.rule.trigger_synonyms || []).join(', ')}</em> → <strong>{matchedModal.rule.add_value}</strong>
                </h3>
                {!matchedModal.loading && matchedModal.items && (
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>
                    {matchedModal.items.length} позиций найдено,{' '}
                    {matchedModal.items.filter(i => !i.is_included).length} исключено
                  </div>
                )}
              </div>
              <button onClick={() => setMatchedModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#999', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '12px 24px' }}>
              {matchedModal.loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Загрузка позиций...</div>
              ) : !matchedModal.items || matchedModal.items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Ни одной позиции не найдено по данным признакам</div>
              ) : (
                <>
                  <div style={{ marginBottom: '10px', display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => setMatchedModal(m => ({ ...m, items: m.items.map(i => ({ ...i, is_included: true })) }))}
                      style={{ fontSize: '13px', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: '#f8f9fa' }}
                    >Выбрать все</button>
                    <button
                      onClick={() => setMatchedModal(m => ({ ...m, items: m.items.map(i => ({ ...i, is_included: false })) }))}
                      style={{ fontSize: '13px', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: '#f8f9fa' }}
                    >Снять все</button>
                  </div>
                  {matchedModal.items.map(item => (
                    <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 4px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={item.is_included}
                        onChange={() => setMatchedModal(m => ({
                          ...m,
                          items: m.items.map(i => i.id === item.id ? { ...i, is_included: !i.is_included } : i)
                        }))}
                        style={{ width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '14px', color: item.is_included ? '#222' : '#aaa' }}>{item.name}</span>
                    </label>
                  ))}
                </>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setMatchedModal(null)} style={{ padding: '8px 18px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: '#fff', fontSize: '14px' }}>
                Отмена
              </button>
              <button
                onClick={saveMatchedExclusions}
                disabled={matchedSaving || matchedModal.loading}
                style={{ padding: '8px 18px', borderRadius: '6px', cursor: 'pointer', background: '#4361ee', color: '#fff', border: 'none', fontSize: '14px' }}
              >
                {matchedSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
